/**
 * Upgrade metadata for every gated integration and feature.
 *
 * Single source of truth for plan tier + benefit copy shown on the UpgradePrompt
 * when a customer lands on a locked feature page.
 *
 * Adding a new integration:
 *   1. Add an entry here keyed by the flagKey used in GatedAdminFeature / GatedSubscriptionFeature
 *   2. Pass it via the meta prop: <GatedAdminFeature flagKey="enableFoo" meta={INTEGRATION_META.enableFoo}>
 *
 * plan: "Professional" | "Enterprise" | "Add-on"
 * benefits: 3–4 short bullets on what the customer actually gets
 * description: one sentence shown under the feature name
 */

export interface IntegrationMeta {
  plan: "Professional" | "Enterprise" | "Add-on";
  description?: string;
  benefits?: string[];
}

export const INTEGRATION_META: Record<string, IntegrationMeta> = {
  // ── Texting ─────────────────────────────────────────────────────────────────

  enablePickupNotifications: {
    plan: "Add-on",
    description:
      "Text customers when their order is ready and automatically remind them on a schedule until it's picked up.",
    benefits: [
      "One click sends a pickup-ready text from the contact record",
      "Optional reminder chain on a cadence you control, up to 14 reminders",
      "Replies land in a normal, agent-routed conversation — not a one-way blast",
      "Requires the SMS channel to be enabled first"
    ]
  },

  // ── CRM ─────────────────────────────────────────────────────────────────────

  enableHubSpot: {
    plan: "Professional",
    description:
      "Sync contacts, log every conversation, and let AI look up deals and tickets directly in chat.",
    benefits: [
      "Auto-create or update HubSpot contacts from every conversation",
      "AI can search contacts, deals, tickets, and companies by name or email",
      "Agents see the full HubSpot record in the sidebar without leaving chat",
      "Activity log entries written back to HubSpot on conversation close"
    ]
  },

  enableSalesforce: {
    plan: "Enterprise",
    description:
      "Connect Salesforce to give AI agents full CRM context and write back leads, cases, and activities.",
    benefits: [
      "AI can search, create, and update leads, contacts, accounts, and cases",
      "Screen pop shows Salesforce record the moment a caller is identified",
      "Agents see Salesforce opportunity data in the chat sidebar",
      "Conversation summaries logged as Salesforce activities automatically"
    ]
  },

  enableDynamics: {
    plan: "Enterprise",
    description:
      "Microsoft Dynamics 365 integration for AI-powered contact lookup, case management, and activity logging.",
    benefits: [
      "AI searches contacts, accounts, leads, and cases across Dynamics 365",
      "Create and update cases or contacts without leaving the conversation",
      "Screen pop identifies callers by phone number against Dynamics records",
      "Full conversation notes logged as Dynamics activities on close"
    ]
  },

  enablePipedrive: {
    plan: "Professional",
    description:
      "Give AI agents access to your Pipedrive pipeline — search deals, contacts, and organizations in real time.",
    benefits: [
      "AI looks up Pipedrive deals and contacts by name, email, or phone",
      "Create or update deals without agents leaving the chat window",
      "Agents see the full deal stage and contact history in the sidebar",
      "Conversation notes added to Pipedrive activities automatically"
    ]
  },

  enableZoho: {
    plan: "Professional",
    description:
      "Integrate Zoho CRM for AI-assisted contact lookup, lead creation, and deal management.",
    benefits: [
      "AI searches Zoho contacts, leads, accounts, and deals during chat",
      "Auto-create leads from new contacts who reach out via chat or IVR",
      "Agents see Zoho deal data without switching tabs",
      "Conversation summaries synced to Zoho as activity notes"
    ]
  },

  enableNetsuite: {
    plan: "Enterprise",
    description:
      "NetSuite ERP integration — AI can look up customers, orders, invoices, and cases in real time.",
    benefits: [
      "AI searches NetSuite customers, orders, and invoices by any field",
      "Create support cases directly in NetSuite from the conversation",
      "Agents see full order history and account balance in the sidebar",
      "High-value account flags surface automatically on inbound contact"
    ]
  },

  // ── E-COMMERCE ───────────────────────────────────────────────────────────────

  enableShopify: {
    plan: "Professional",
    description:
      "Give AI agents full Shopify access — orders, products, customers, and refunds without leaving chat.",
    benefits: [
      "AI can look up orders, track shipments, and process refunds in chat",
      "Product search lets agents send catalog items directly to customers",
      "Customer history, LTV, and order count shown on every conversation",
      "Abandoned cart recovery workflows trigger from AI conversation events"
    ]
  },

  enableBigCommerce: {
    plan: "Professional",
    description:
      "BigCommerce integration for AI-powered order lookup, product search, and customer management.",
    benefits: [
      "AI searches orders, products, and customers across your BigCommerce store",
      "Order status, tracking, and refund tools available directly in chat",
      "Customer purchase history surfaces automatically in agent sidebar",
      "Proactive abandoned-cart messages triggered by BigCommerce webhooks"
    ]
  },

  enableMagento: {
    plan: "Professional",
    description:
      "Adobe Commerce / Magento integration for real-time order management and product catalog access.",
    benefits: [
      "AI looks up Magento orders, customers, and products during chat",
      "Order status and return processing without agents leaving Velaro",
      "Product catalog search with images surfaced to customers in chat",
      "Customer account details and order history in the agent sidebar"
    ]
  },

  enableWooCommerce: {
    plan: "Professional",
    description:
      "WooCommerce integration — AI-powered order tracking, refunds, and product lookup for your WordPress store.",
    benefits: [
      "AI retrieves WooCommerce order status and tracking in real time",
      "Refund and cancellation requests handled without leaving chat",
      "Customer purchase history and LTV shown in the agent sidebar",
      "Proactive order-shipped and delivery-delay workflows"
    ]
  },

  // ── FIELD SERVICE / SCHEDULING ───────────────────────────────────────────────

  enableCallRail: {
    plan: "Professional",
    description:
      "Connect CallRail so every inbound IVR call arrives with marketing attribution — source, campaign, and medium.",
    benefits: [
      "See which ad, campaign, or channel drove every phone call",
      "Call recordings linked directly to the attribution record",
      "Report ready to extend with additional attribution providers over time",
      "No changes required to your existing CallRail tracking numbers"
    ]
  },

  enableJobber: {
    plan: "Professional",
    description:
      "Jobber integration for field service teams — AI can book jobs, look up clients, and manage schedules.",
    benefits: [
      "AI books, reschedules, and cancels Jobber jobs directly in chat",
      "Client history, open quotes, and job status shown on every conversation",
      "IVR callers can check job status or request reschedules 24/7",
      "New leads from chat automatically created as Jobber requests"
    ]
  },

  enableServiceTitan: {
    plan: "Enterprise",
    description:
      "ServiceTitan integration for trades and home services — job booking, dispatch, and customer lookup.",
    benefits: [
      "AI books and reschedules ServiceTitan jobs without agent involvement",
      "Customer history, equipment records, and open jobs in the sidebar",
      "IVR handles appointment confirmations and same-day rescheduling",
      "Technician dispatch status surfaced to customers via chat or IVR"
    ]
  },

  enableMindbody: {
    plan: "Professional",
    description:
      "Mindbody integration for wellness and fitness businesses — class booking, membership lookup, and scheduling.",
    benefits: [
      "AI books classes, appointments, and services directly in chat",
      "Members can check schedules, cancel, or reschedule via IVR 24/7",
      "Membership status and visit history shown to agents in the sidebar",
      "New class availability triggers proactive outreach to waitlisted members"
    ]
  },

  enableMicrosoftBookingsSkills: {
    plan: "Professional",
    description:
      "Microsoft Bookings integration — AI can check availability and book appointments across your team.",
    benefits: [
      "AI finds open slots and books Microsoft Bookings appointments in chat",
      "Customers can reschedule or cancel without waiting for an agent",
      "IVR handles appointment confirmation and reminder calls automatically",
      "Booking confirmations sent to both customer and staff immediately"
    ]
  },

  enableAppointments: {
    plan: "Professional",
    description:
      "Velaro Appointments — text-to-book AI across Calendly, Outlook, and Microsoft Bookings in one place.",
    benefits: [
      "Customers book appointments via chat, IVR, or SMS without agent help",
      "AI checks availability across multiple calendars and staff members",
      "Automated confirmation, reminder, and follow-up messages included",
      "Works with Calendly, Outlook Calendar, and Microsoft Bookings"
    ]
  },

  // ── HOSPITALITY / PROPERTY MANAGEMENT ────────────────────────────────────────

  enableGuesty: {
    plan: "Enterprise",
    description:
      "Guesty PMS integration for short-term rental managers — reservation lookup, guest messaging, and ops AI.",
    benefits: [
      "AI looks up reservations, guest details, and property info in real time",
      "Guests can check in/out times, request amenities, and report issues via chat",
      "Maintenance and housekeeping requests created in Guesty automatically",
      "IVR handles after-hours guest calls with full reservation context"
    ]
  },

  enableUplisting: {
    plan: "Enterprise",
    description:
      "Uplisting channel manager integration for multi-channel vacation rental management.",
    benefits: [
      "AI accesses Uplisting reservation and guest data during conversations",
      "Guests can get check-in instructions and property details via chat or IVR",
      "Support tickets from guests automatically linked to their reservation",
      "Multi-property management — AI knows which property each guest booked"
    ]
  },

  enableCloudbeds: {
    plan: "Enterprise",
    description:
      "Cloudbeds PMS integration for hotels and lodging — reservation management and guest experience AI.",
    benefits: [
      "AI looks up reservations, room details, and guest preferences in chat",
      "Guests can request room service, report issues, or extend stays via IVR",
      "Check-in instructions and local recommendations delivered automatically",
      "Housekeeping and maintenance requests logged to Cloudbeds in real time"
    ]
  },

  // ── POINT OF SALE ────────────────────────────────────────────────────────────

  enableCloverSkills: {
    plan: "Professional",
    description:
      "Clover POS integration — AI can look up orders, loyalty points, and customer purchase history.",
    benefits: [
      "AI searches Clover orders and customer records during chat",
      "Loyalty points balance and redemption options shown in conversation",
      "Order status and pickup times provided by IVR without staff involvement",
      "Customer purchase history in the agent sidebar for personalized service"
    ]
  },

  enableToast: {
    plan: "Professional",
    description:
      "Toast POS integration for restaurants — order lookup, reservations, and loyalty in chat and IVR.",
    benefits: [
      "AI answers order status and estimated wait time questions in real time",
      "IVR handles table reservation requests and confirmations 24/7",
      "Loyalty point balance and reward redemption available in chat",
      "Guest history and preferences surface to agents on every conversation"
    ]
  },

  // ── PRODUCTIVITY / PROJECT MANAGEMENT ────────────────────────────────────────

  enableAirtable: {
    plan: "Professional",
    description:
      "Airtable integration — AI can query, create, and update records across your connected bases.",
    benefits: [
      "AI searches and updates Airtable records during customer conversations",
      "New records created automatically from chat, form, or IVR intake",
      "Agents see linked Airtable data in the sidebar without switching tabs",
      "Workflow triggers fire on Airtable record changes via webhook"
    ]
  },

  enableMonday: {
    plan: "Professional",
    description:
      "Monday.com integration — AI can look up items, create tasks, and update boards from chat.",
    benefits: [
      "AI creates and updates Monday.com items directly from conversations",
      "Agents see project status and assigned tasks in the chat sidebar",
      "Customer requests automatically open items on the right board",
      "Status updates from Monday.com trigger proactive customer notifications"
    ]
  },

  // ── FORMS / AUTOMATION ───────────────────────────────────────────────────────

  enableFormstack: {
    plan: "Professional",
    description:
      "Formstack integration — trigger forms, collect structured data, and push submissions to your workflows.",
    benefits: [
      "Send Formstack forms to customers mid-conversation for structured intake",
      "Form submissions automatically trigger Velaro workflows",
      "Pre-fill form fields with data already known from the conversation",
      "Submission data synced to connected CRMs and help desks"
    ]
  },

  // ── VOICE / PHONE ────────────────────────────────────────────────────────────

  enableNextiva: {
    plan: "Enterprise",
    description:
      "Nextiva UCaaS integration — screen pop, call controls, and CRM sync across your Nextiva phone system.",
    benefits: [
      "Caller ID matched to CRM records with instant screen pop on inbound calls",
      "Click-to-call from any contact record in Velaro",
      "Call recordings and transcripts automatically attached to conversations",
      "IVR and Velaro chat unified in one conversation thread"
    ]
  },

  EnableVelaroPhone: {
    plan: "Add-on",
    description:
      "Get a dedicated Velaro Voice number provisioned automatically, no carrier account or hardware required.",
    benefits: [
      "Self-serve phone number provisioning, no support ticket needed",
      "Screen pop on every inbound call across all connected CRM/e-commerce integrations",
      "Multi-tenant / reseller support for agencies managing multiple businesses",
      "IVR hand-off with full conversation context"
    ]
  },

  TeamsPhone: {
    plan: "Enterprise",
    description:
      "Microsoft Teams Phone integration - screen pop, call controls, and CRM sync across your Teams voice deployment.",
    benefits: [
      "Caller ID matched to CRM records with instant screen pop on inbound Teams calls",
      "Works with Calling Plan, Direct Routing, or Operator Connect Teams voice deployments",
      "Call recordings and transcripts automatically attached to conversations",
      "IVR and Velaro chat unified in one conversation thread"
    ]
  },

  EnableZoomPhoneScreenPop: {
    plan: "Add-on",
    description:
      "Zoom Phone passive screen pop with customer context while calls remain in Zoom Phone.",
    benefits: [
      "Caller ID matched to CRM records with instant screen pop on inbound Zoom Phone calls",
      "NetSuite customer context shown when a matching record is found",
      "Admin-managed Zoom user mapping for reception and direct-call teams",
      "Passive lifecycle updates in Velaro web and foreground mobile sessions"
    ]
  },

  EnableZoomContactCenterScreenPop: {
    plan: "Add-on",
    description:
      "Zoom Contact Center passive screen pop for queued voice engagements, with customer context in Velaro while audio and routing remain in Zoom.",
    benefits: [
      "Inbound Contact Center voice reservations matched to CRM records",
      "NetSuite customer context shown when a matching record is found",
      "Separate Contact Center agent mapping that can coexist with Zoom Phone",
      "Auditable connection and agent-mapping changes for a controlled pilot rollout"
    ]
  },

  // ── GOVERNMENT / CIVIC ───────────────────────────────────────────────────────

  enableAccela: {
    plan: "Enterprise",
    description:
      "Accela Civic Platform integration for government agencies — permit lookup, case status, and public services AI.",
    benefits: [
      "Residents can check permit status, case updates, and fees 24/7 via IVR",
      "AI looks up Accela records and routes callers to the right department",
      "New service requests created from chat or IVR without staff involvement",
      "Multilingual IVR handles high call volumes during peak permit season"
    ]
  },

  // ── SECURITY / COMPLIANCE ────────────────────────────────────────────────────

  enableSecureForms: {
    plan: "Add-on",
    description:
      "Secure Forms — collect sensitive data (SSN, card numbers, DOB) without exposing it in the chat transcript.",
    benefits: [
      "PCI-compliant field collection — sensitive data never touches Velaro servers",
      "Agents can request secure form mid-conversation with one click",
      "Submitted data delivered directly to your system via encrypted webhook",
      "Full audit trail of form requests and completions for compliance"
    ]
  },

  // ── MARKETING / CMS ──────────────────────────────────────────────────────────

  enableWebflow: {
    plan: "Professional",
    description:
      "Webflow CMS integration — trigger chat widgets from CMS events and sync lead data back to Webflow.",
    benefits: [
      "Chat widget configuration managed from Webflow CMS without code deploys",
      "Lead capture from Velaro chat synced to Webflow form submissions",
      "CMS content changes trigger proactive customer outreach workflows",
      "Embed Velaro chat on any Webflow page with automatic site detection"
    ]
  },

  // ── RECRUITING ───────────────────────────────────────────────────────────────

  enableRecruitingBot: {
    plan: "Professional",
    description:
      "AI recruiting bot — screen candidates, schedule interviews, and collect application data automatically.",
    benefits: [
      "AI screens candidates with custom questions before routing to a recruiter",
      "Interview scheduling integrated with your team's calendar availability",
      "Candidate data collected and exported to your ATS automatically",
      "IVR handles after-hours candidate inquiries and status check calls"
    ]
  },

  // ── PRODUCTIVITY ─────────────────────────────────────────────────────────────

  enableNotion: {
    plan: "Professional",
    description:
      "Turn your Notion workspace into a live AI knowledge base and auto-capture leads as Notion database entries.",
    benefits: [
      "AI searches your Notion pages in real time to answer visitor questions",
      "Lead records created automatically in a Notion database when visitors qualify",
      "Zero KB duplication — update Notion and the bot immediately knows",
      "Works with any Notion internal integration token — no OAuth required"
    ]
  },

  // ── RETAIL / ANALYTICS ───────────────────────────────────────────────────────

  enableVast: {
    plan: "Enterprise",
    description:
      "VAST Retail integration for automotive and powersports dealerships — inventory and lead management.",
    benefits: [
      "AI searches VAST vehicle inventory by make, model, year, and price",
      "Shoppers get availability and pricing in real time via chat or IVR",
      "Leads created in VAST automatically from qualifying chat conversations",
      "Trade-in and financing inquiries routed to the right desk immediately"
    ]
  },

  // ── CALLBACK SCHEDULING ───────────────────────────────────────────────────────────────────

  EnableCallbackScheduling: {
    plan: "Professional",
    description:
      "One decision engine for every callback request — IVR, widget, and (soon) web forms — instead of each channel hardcoding its own path.",
    benefits: [
      "Auto-dial when an agent is free, or at the customer's requested time",
      "Offer internal availability slots or hand off to an external calendar",
      "Escalate to a high-priority ticket when there's no VoIP connected",
      "Built-in abuse protection — per-site and per-IP rate limits, phone dedup"
    ]
  }
};
